export interface TrialState {
  principalKind: 'anonymous';
  limit: number; // 0 means unlimited anonymous scans.
  scansUsed: number;
  scansRemaining: number; // Neutral 0 when unlimited; inspect limit for quota status.
  expiresAt: string;
  nextScanIsLast: boolean;
  exhausted: boolean;
  conversionPath: string;
}
export type SessionState =
  | TrialState
  | { principalKind: 'authenticated'; conversionPath: string };
interface Dependencies {
  read: () => Promise<SessionState>;
  bootstrap: () => Promise<SessionState>;
  claim: () => Promise<{ claimed: boolean }>;
  load: (checkIdentity: () => void) => Promise<void>;
  begin: () => void;
  complete: (state: SessionState) => void;
  fail: (error: unknown) => void;
  lock: <T>(work: () => Promise<T>) => Promise<T>;
}
export function createSessionCoordinator(deps: Dependencies) {
  let identity: string | null = null;
  let generation = 0;
  let claimedIdentity: string | null = null;
  let pending: Promise<{ claimed: boolean }> | null = null;
  const invalidate = () => {
    generation++;
    identity = null;
    pending = null;
    claimedIdentity = null;
  };
  return {
    invalidate,
    start(nextIdentity: string) {
      if (identity === nextIdentity && pending) return pending;
      identity = nextIdentity;
      const current = ++generation;
      const check = () => {
        if (current !== generation) throw new Error('Session identity changed');
      };
      deps.begin();
      pending = (async () => {
        try {
          let claimed = false;
          let state: SessionState;
          if (nextIdentity !== 'anonymous') {
            const result = await deps.claim();
            check();
            if (result.claimed) claimedIdentity = nextIdentity;
            claimed = claimedIdentity === nextIdentity;
            state = await deps.read();
          } else {
            state = await deps.lock(async () => {
              check();
              try {
                return await deps.read();
              } catch (error) {
                check();
                const response = (
                  error as {
                    response?: { status?: number; data?: { error?: string } };
                  }
                ).response;
                if (
                  response?.status === 401 &&
                  response.data?.error === 'missing session'
                )
                  return deps.bootstrap();
                throw error;
              }
            });
          }
          check();
          await deps.load(check);
          check();
          deps.complete(state);
          return { claimed };
        } catch (error) {
          if (current === generation) {
            pending = null;
            deps.fail(error);
          }
          throw error;
        }
      })();
      return pending;
    },
  };
}
export function trialError(error: unknown): {
  code?: string;
  trial?: TrialState;
  conversionPath?: string;
} {
  const data = (
    error as {
      response?: {
        data?: { code?: string; trial?: TrialState; conversionPath?: string };
      };
    }
  )?.response?.data;
  return {
    code: data?.code,
    trial: data?.trial,
    conversionPath: data?.conversionPath,
  };
}
export function safeConversionPath(path?: string) {
  return path && /^\/(?!\/)[A-Za-z0-9_./-]*$/.test(path)
    ? path
    : '/create-account';
}
